// src/components/manager-v2/ManagerPortal.js
import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ManagerSidebar from './ManagerSidebar';
import ManagerHeader from './ManagerHeader';
import ManagerDashboard from './ManagerDashboard';
import TeamApprovals from './TeamApprovals';
import ManagerOrgView from './ManagerOrgView';
import { useAuth } from '../../hooks/useAuth';

// Import Employee Portal components to reuse for manager's personal sections
import EmployeeProfile from '../employee/EmployeeProfile';
import ComprehensiveOnboarding from '../employee/OnboardingWizardRedesign';
import EmployeeAttendance from '../employee/EmployeeAttendance';
import EmployeeLeaves from '../employee/EmployeeLeaves';
import EmployeePerformance from '../employee/EmployeePerformance';
import EmployeePayroll from '../employee/EmployeePayroll';
import EmployeeDocuments from '../employee/EmployeeDocumentsEnhanced';

// Import Team Management components
import TeamManagement from './TeamManagement';
import TeamAttendanceManagement from './TeamAttendanceManagement';
import TeamPerformance from './TeamPerformance';
import TeamReports from './TeamReports';

// Import common components
import EmployeeProfileViewWrapper from '../common/EmployeeProfileViewWrapper';
import { TaskBox } from '../common/taskbox';
import { VibeFeed } from '../common/vibe';
import SettingsContent from '../common/SettingsContent';
import AIAvatarContainer from '../common/aia/AIAvatarContainer';

// Import Team Taskbox
import TeamTaskbox from './teamTaskbox/TeamTaskbox';

// Import Team Flow Automation
import TeamFlowAutomation from './flows/TeamFlowAutomation';

const ManagerPortal = () => {
    const { user, logout } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    return (
        <div className="h-screen flex bg-gray-100 dark:bg-gray-900 overflow-hidden">
            <ManagerSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                user={user}
            />

            <div className="flex-1 flex flex-col min-w-0">
                <ManagerHeader
                    user={user}
                    onLogout={logout}
                />

                <main className="flex-1 p-6 overflow-y-auto">
                    <Routes>
                        <Route path="/" element={<Navigate to="/manager/dashboard" />} />
                        <Route path="/dashboard" element={<ManagerDashboard />} />

                        {/* Personal Section Routes - Using Employee Portal Components */}
                        <Route path="/profile" element={<EmployeeProfile />} />
                        <Route path="/onboarding" element={<ComprehensiveOnboarding />} />
                        <Route path="/attendance" element={<EmployeeAttendance />} />
                        <Route path="/leaves" element={<EmployeeLeaves />} />
                        <Route path="/performance" element={<EmployeePerformance />} />
                        <Route path="/payroll" element={<EmployeePayroll />} />
                        <Route path="/documents" element={<EmployeeDocuments />} />
                        <Route path="/org-view" element={<ManagerOrgView />} />
                        <Route path="/taskbox" element={<TaskBox userRole="manager" />} />
                        <Route path="/team-taskbox" element={<TeamTaskbox />} />
                        <Route path="/vibe" element={<VibeFeed />} />
                        <Route path="/settings" element={<SettingsContent />} />

                        {/* Team Management Section Routes */}
                        <Route path="/team-management" element={<TeamManagement />} />
                        <Route path="/team-management/:id" element={<EmployeeProfileViewWrapper backPath="/manager/team-management" />} />
                        <Route path="/view-profile/:id" element={<EmployeeProfileViewWrapper backPath="/manager/dashboard" />} />
                        <Route path="/team-attendance" element={<TeamAttendanceManagement />} />
                        <Route path="/team-approvals" element={<TeamApprovals />} />
                        <Route path="/team-performance" element={<TeamPerformance />} />
                        <Route path="/team-reports" element={<TeamReports />} />
                        <Route path="/flows/*" element={<TeamFlowAutomation />} />
                    </Routes>
                </main>
            </div>

            {/* AI Avatar & FTUE */}
            <AIAvatarContainer />
        </div>
    );
};

export default ManagerPortal;
